import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { features, testimonials, pricingPlans } from '../mock';

// Testimonial Carousel Component
const TestimonialCarousel = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const featuredTestimonials = testimonials.slice(0, 3);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % featuredTestimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [featuredTestimonials.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % featuredTestimonials.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + featuredTestimonials.length) % featuredTestimonials.length);
  };

  const current = featuredTestimonials[currentIndex];

  return (
    <div className="relative">
      <div className="bg-gradient-to-br from-[#0066FF]/20 to-[#0052CC]/10 rounded-3xl p-8 md:p-12 border border-[#0066FF]/30 backdrop-blur">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <div className="flex mb-6">
              {[...Array(current?.rating || 5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <blockquote className="text-2xl md:text-3xl font-medium text-white leading-relaxed mb-8 min-h-[120px]">
              "{current?.content || "HRMQore didn't just change our processes—it transformed our entire workplace culture."}"
            </blockquote>
            <div className="flex items-center space-x-4">
              <div>
                <div className="font-semibold text-white text-lg">{current?.name || "Customer"}</div>
                <div className="text-gray-400">
                  {current?.position || "Position"}, {current?.company || "Company"}
                </div>
              </div>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#0066FF] to-[#0052CC] rounded-2xl opacity-20 blur-xl"></div>
              <div className="relative bg-gray-800 rounded-2xl p-6 border border-gray-700">
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#0066FF] mb-2">60%</div>
                  <div className="text-gray-400 text-sm">Hours Saved Monthly</div>
                </div>
                <div className="my-6 h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
                <div className="text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">50%</div>
                  <div className="text-gray-400 text-sm">More Strategic Focus</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-center mt-6 space-x-4">
        <button 
          onClick={goToPrev}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex space-x-2">
          {featuredTestimonials.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                idx === currentIndex ? 'bg-[#0066FF] w-8' : 'bg-white/30 hover:bg-white/50'
              }`}
            />
          ))}
        </div>
        <button 
          onClick={goToNext}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const Home = () => {
  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Premium Dark Design */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[100vh] md:min-h-[85vh] flex items-center bg-gradient-to-b from-gray-900 to-gray-800">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0066FF 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0066FF 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-10 right-10 w-32 md:w-64 h-32 md:h-64 bg-[#0066FF]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-40 md:w-80 h-40 md:h-80 bg-[#0052CC]/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Text Content */}
            <div className="space-y-4 md:space-y-6 text-center lg:text-left">
              <Badge className="bg-white/10 text-white hover:bg-white/20 border-0 backdrop-blur text-xs md:text-sm">
                Trusted by leading people teams
              </Badge>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
                Your People at the{' '}
                <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
                  Qore
                </span>{' '}
                of Your Business Growth
              </h1>
              <p className="text-sm md:text-lg text-gray-300 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Transform your HR operations with intelligent, integrated solutions. From recruitment to payroll, manage your entire workforce lifecycle in one powerful platform.
              </p>
              <div className="flex justify-center lg:justify-start">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90 transition-all duration-200 group text-sm md:text-base"
                  onClick={() => window.location.href = '/contact'}
                >
                  Request Demo
                  <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>

            {/* Right - Premium Glass Cards */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8 lg:mt-0">
              {/* Performance Analytics Card - Premium Glass with Transparent Background */}
              <div className="flex-1 bg-transparent rounded-2xl overflow-hidden border border-[#0066FF]/30 backdrop-blur-xl p-4 md:p-5">
                <h3 className="text-xs md:text-sm font-semibold text-gray-300 mb-4 md:mb-5">Performance Analytics</h3>
                <div className="space-y-4">
                  {/* Metric Row 1 - Bigger pie chart */}
                  <div className="flex items-center space-x-4 md:space-x-5 p-3 md:p-3.5 bg-transparent rounded-lg border border-white/10">
                    <div className="relative w-16 h-16 md:w-20 md:h-20 flex-shrink-0">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#0066FF" strokeWidth="8" 
                          strokeDasharray="251.3" strokeDashoffset="0" strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center bg-transparent">
                        <span className="text-sm md:text-base font-bold text-[#60a5fa]">100%</span>
                      </div>
                    </div>
                    <span className="text-sm md:text-base font-medium text-gray-200">Self serviced</span>
                  </div>
                  {/* Metric Row 2 - Bigger pie chart */}
                  <div className="flex items-center space-x-4 md:space-x-5 p-3 md:p-3.5 bg-transparent rounded-lg border border-white/10">
                    <div className="relative w-16 h-16 md:w-20 md:h-20 flex-shrink-0">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#10b981" strokeWidth="8" 
                          strokeDasharray="251.3" strokeDashoffset="0" strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center bg-transparent">
                        <span className="text-sm md:text-base font-bold text-green-400">100%</span>
                      </div>
                    </div>
                    <span className="text-sm md:text-base font-medium text-gray-200">Payroll Processing</span>
                  </div>
                  {/* Metric Row 3 - Bigger pie chart */}
                  <div className="flex items-center space-x-4 md:space-x-5 p-3 md:p-3.5 bg-transparent rounded-lg border border-white/10">
                    <div className="relative w-16 h-16 md:w-20 md:h-20 flex-shrink-0">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#f59e0b" strokeWidth="8" 
                          strokeDasharray="251.3" strokeDashoffset="0" strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center bg-transparent">
                        <span className="text-sm md:text-base font-bold text-amber-400">100%</span>
                      </div>
                    </div>
                    <span className="text-sm md:text-base font-medium text-gray-200">Employee Onboarding</span>
                  </div>
                </div>
              </div>

              {/* Employee Workflow Card - Premium Glass */}
              <div className="flex-1 bg-gradient-to-br from-[#0066FF]/20 to-[#0052CC]/10 rounded-2xl p-4 md:p-5 border border-[#0066FF]/30 backdrop-blur-xl">
                <div className="flex items-center justify-between mb-3 md:mb-5">
                  <h3 className="text-xs md:text-sm font-semibold text-gray-300">Employee Workflow</h3>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px] md:text-xs px-2 py-0.5">Active</Badge>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Employee Onboarding", icon: "UserPlus" },
                    { label: "Documentation Review", icon: "FileText" },
                    { label: "Data Management", icon: "Database" },
                    { label: "Wallet Setup", icon: "Wallet" },
                    { label: "Payroll Processing", icon: "DollarSign" }
                  ].map((step, idx) => {
                    const StepIcon = LucideIcons[step.icon];
                    return (
                      <div
                        key={idx}
                        className="flex items-center space-x-2 md:space-x-3 p-2 md:p-2.5 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors"
                      >
                        <div className="w-6 h-6 md:w-7 md:h-7 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-md flex items-center justify-center flex-shrink-0">
                          {StepIcon && <StepIcon className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" />}
                        </div>
                        <span className="flex-1 font-medium text-gray-200 text-[10px] md:text-xs">{step.label}</span>
                        <div className="w-4 h-4 md:w-5 md:h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Metrics Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm font-semibold text-[#0066FF] uppercase tracking-wider mb-4">
            HRMQore IMPACT AT A GLANCE
          </p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Measurable results that{' '}
            <span className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] bg-clip-text text-transparent">
              drive success
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
            Real metrics from companies using HRMQore to transform their people operations
          </p>
          
          {/* Metrics Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#0066FF] mb-2">60%</div>
              <div className="text-base text-gray-600">Time Saved</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#0066FF] mb-2">5x</div>
              <div className="text-base text-gray-600">Faster Onboarding</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#0066FF] mb-2">3x</div>
              <div className="text-base text-gray-600">Faster Decisions</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#0066FF] mb-2">50%</div>
              <div className="text-base text-gray-600">More Strategic Focus</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image Section (centered and larger) */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src="/media/premium_photo-1663040599482-380605615525.avif"
              alt="Professional working with HRMQore"
              className="w-full h-auto object-cover"
              style={{ aspectRatio: '16/9' }}
            />
          </div>
        </div>
      </section>

      {/* Leave Management - Detailed Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#e0f2fe] via-white to-[#f0f9ff] -z-10" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#0066FF]/10 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#0052CC]/10 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6 text-center lg:text-left">
              <div>
                <p className="text-sm font-semibold text-[#0066FF] uppercase tracking-wider mb-3">
                  LEAVE & ATTENDANCE
                </p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Effortless time management{' '}
                  <span className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] bg-clip-text text-transparent">
                    your team will love
                  </span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Say goodbye to spreadsheets and manual tracking. Our intelligent leave and attendance system automates approvals, tracks time seamlessly, and gives both managers and employees complete visibility into schedules and balances.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-2 border-gray-100 text-center lg:text-left">
                  <CardHeader>
                    <div className="w-12 h-12 bg-[#0066FF]/10 rounded-lg flex items-center justify-center mb-3 mx-auto lg:mx-0">
                      <LucideIcons.Calendar className="w-6 h-6 text-[#0066FF]" />
                    </div>
                    <CardTitle className="text-base">Automated Leave Workflows</CardTitle>
                    <CardDescription className="text-sm">
                      Intelligent request routing with instant balance updates and policy compliance checks.
                    </CardDescription>
                  </CardHeader>
                </Card>
                <Card className="border-2 border-gray-100 text-center lg:text-left">
                  <CardHeader>
                    <div className="w-12 h-12 bg-[#0066FF]/10 rounded-lg flex items-center justify-center mb-3 mx-auto lg:mx-0">
                      <LucideIcons.Clock className="w-6 h-6 text-[#0066FF]" />
                    </div>
                    <CardTitle className="text-base">Smart Time Tracking</CardTitle>
                    <CardDescription className="text-sm">
                      Biometric and geolocation support with automated timesheet generation and reporting.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90"
                  onClick={() => window.location.href = '/contact'}
                >
                  Request Demo
                </Button>
              </div>
            </div>

            {/* Dashboard Mockup */}
            <div className="relative">
              <div className="bg-[#f0f5ff] rounded-3xl p-6 shadow-xl">
                {/* Leave Info Card */}
                <div className="bg-white rounded-2xl p-5 mb-4 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">Leave Info</h4>
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <LucideIcons.MoreHorizontal className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                  <div className="space-y-3">
                    {[
                      { name: "Adaora Nwosu", status: "On Leave", statusColor: "text-green-500", date: "12/25 - 01/02" },
                      { name: "Emeka Obi", status: "On Leave", statusColor: "text-green-500", date: "12/26 - 12/30" },
                      { name: "Funmi Adeyemi", status: "Pending Approval", statusColor: "text-amber-500", date: "01/05 - 01/10" }
                    ].map((employee, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                            {employee.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{employee.name}</div>
                            <div className={`text-xs ${employee.statusColor}`}>{employee.status}</div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">{employee.date}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Attendance Chart */}
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">Attendance Overview</h4>
                    <select className="text-xs bg-gray-100 border-0 rounded-lg px-2 py-1">
                      <option>Monthly</option>
                    </select>
                  </div>
                  {/* Pie Chart Visualization */}
                  <div className="flex items-center justify-center mb-4">
                    <div className="relative w-32 h-32">
                      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="20" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#0066FF" strokeWidth="20" strokeDasharray="188.5 251.3" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#f472b6" strokeWidth="20" strokeDasharray="50.3 251.3" strokeDashoffset="-188.5" />
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#fb923c" strokeWidth="20" strokeDasharray="12.6 251.3" strokeDashoffset="-238.8" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex justify-center space-x-4 text-xs">
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-[#0066FF] rounded-full"></div>
                      <span className="text-gray-600">Present 75%</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-pink-400 rounded-full"></div>
                      <span className="text-gray-600">Absent 20%</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                      <span className="text-gray-600">Late 5%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payroll Section - Sophisticated Wallet & Salary Dashboard */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center text-center md:text-left">
            {/* Left - Wallet & Salary Dashboard Mockup */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-[#f8fafc] rounded-3xl p-6 shadow-xl">
                {/* Wallet Card - Blue Background */}
                <div className="bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-2xl p-5 mb-4 shadow-lg text-white">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white/80">Wallet ID: 0386708***</span>
                    <div className="flex items-center space-x-2">
                      <LucideIcons.Bell className="w-4 h-4 text-white/60" />
                      <LucideIcons.User className="w-4 h-4 text-white/60" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs text-white/70">Available Balance</span>
                    <LucideIcons.Eye className="w-3 h-3 text-white/50" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-4">₦4,250,000.00</div>
                  <div className="flex space-x-2">
                    <button className="flex-1 py-2 px-3 bg-white/20 backdrop-blur border border-white/30 rounded-lg text-sm font-medium text-white hover:bg-white/30 transition-colors">
                      Withdraw
                    </button>
                    <button className="flex-1 py-2 px-3 bg-white/20 backdrop-blur border border-white/30 rounded-lg text-sm font-medium text-white hover:bg-white/30 transition-colors">
                      Transfer
                    </button>
                    <button className="flex-1 py-2 px-3 bg-white/20 backdrop-blur border border-white/30 rounded-lg text-sm font-medium text-white hover:bg-white/30 transition-colors">
                      Quick Cash
                    </button>
                  </div>
                </div>

                {/* Employee Salary Table */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900 text-sm">Employee Salary</h4>
                    <div className="flex space-x-2">
                      <button className="px-3 py-1 text-xs border border-gray-200 rounded-lg text-gray-600">Import</button>
                      <button className="px-3 py-1 text-xs bg-[#0066FF] text-white rounded-lg">Export</button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: "Adaora N.", id: "EMP001", dept: "Engineering", grade: "Senior", salary: "₦850,000" },
                      { name: "Emeka O.", id: "EMP002", dept: "Marketing", grade: "Executive", salary: "₦650,000" },
                      { name: "Funmi A.", id: "EMP003", dept: "Operations", grade: "Officer", salary: "₦450,000" }
                    ].map((emp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-full flex items-center justify-center text-white font-semibold text-xs">
                            {emp.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 text-xs">{emp.name}</div>
                            <div className="text-xs text-gray-500">{emp.id} • {emp.dept}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-semibold text-gray-900">{emp.salary}</div>
                          <div className="text-xs text-gray-500">{emp.grade}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Transaction History Preview */}
                <div className="bg-white rounded-2xl p-4 mt-4 shadow-sm border border-gray-100">
                  <h4 className="font-semibold text-gray-900 text-sm mb-3">Recent Transactions</h4>
                  <div className="space-y-2">
                    {[
                      { desc: "Salary Payment", amount: "+₦850,000", status: "COMPLETED", color: "text-green-600", bg: "bg-green-50" },
                      { desc: "Withdrawal", amount: "-₦50,000", status: "COMPLETED", color: "text-gray-600", bg: "bg-gray-50" },
                      { desc: "Quick Cash", amount: "+₦200,000", status: "PENDING", color: "text-amber-600", bg: "bg-amber-50" }
                    ].map((tx, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-2 ${tx.bg} rounded-lg`}>
                        <div>
                          <div className="text-xs font-medium text-gray-900">{tx.desc}</div>
                          <div className={`text-xs ${tx.color}`}>{tx.status}</div>
                        </div>
                        <div className={`text-sm font-semibold ${tx.amount.startsWith('+') ? 'text-green-600' : 'text-gray-700'}`}>
                          {tx.amount}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="space-y-6 order-1 lg:order-2 text-center lg:text-left">
              <div>
                <p className="text-sm font-semibold text-[#0066FF] uppercase tracking-wider mb-3">
                  EMPLOYEE PAYROLL & WALLET
                </p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Payroll processing made{' '}
                  <span className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] bg-clip-text text-transparent">
                    simple and accurate
                  </span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Process payroll in minutes, not hours. Our comprehensive payroll engine handles everything from salary calculations to tax deductions, with a built-in digital wallet system for seamless fund management.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-2 border-gray-100 text-center lg:text-left">
                  <CardHeader>
                    <div className="w-12 h-12 bg-[#0066FF]/10 rounded-lg flex items-center justify-center mb-3 mx-auto lg:mx-0">
                      <LucideIcons.Wallet className="w-6 h-6 text-[#0066FF]" />
                    </div>
                    <CardTitle className="text-base">Digital Wallet System</CardTitle>
                    <CardDescription className="text-sm">
                      Secure in-app wallets with instant withdrawals, transfers, and quick cash options.
                    </CardDescription>
                  </CardHeader>
                </Card>
                <Card className="border-2 border-gray-100 text-center lg:text-left">
                  <CardHeader>
                    <div className="w-12 h-12 bg-[#0066FF]/10 rounded-lg flex items-center justify-center mb-3 mx-auto lg:mx-0">
                      <LucideIcons.FileText className="w-6 h-6 text-[#0066FF]" />
                    </div>
                    <CardTitle className="text-base">Automated Payslips</CardTitle>
                    <CardDescription className="text-sm">
                      Instant generation and delivery of detailed payslips to all team members.
                    </CardDescription>
                  </CardHeader>
                </Card>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90"
                  onClick={() => window.location.href = '/contact'}
                >
                  Request Demo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recruitment Section - Hub & Spoke Design */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#e0f2fe] via-white to-[#f0f9ff] -z-10" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#0066FF]/10 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#0052CC]/10 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-center lg:text-left">
              <div>
                <p className="text-sm font-semibold text-[#0066FF] uppercase tracking-wider mb-3">
                  RECRUITMENT & ONBOARDING
                </p>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Build exceptional teams{' '}
                  <span className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] bg-clip-text text-transparent">
                    with smart hiring
                  </span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Transform your hiring process from job posting to day-one readiness. Our integrated recruitment tools help you find the right talent quickly and onboard them seamlessly for immediate impact.
                </p>
              </div>
              <div className="space-y-4">
                <div className="flex flex-col items-center lg:items-start lg:flex-row lg:space-x-3 text-center lg:text-left">
                  <CheckCircle className="w-6 h-6 text-[#0066FF] flex-shrink-0 mb-2 lg:mb-0 lg:mt-1" />
                  <div>
                    <h4 className="font-semibold text-base mb-1">Intelligent Candidate Tracking</h4>
                    <p className="text-sm text-gray-600">Streamline applications and manage candidates efficiently throughout the hiring journey.</p>
                  </div>
                </div>
                <div className="flex flex-col items-center lg:items-start lg:flex-row lg:space-x-3 text-center lg:text-left">
                  <CheckCircle className="w-6 h-6 text-[#0066FF] flex-shrink-0 mb-2 lg:mb-0 lg:mt-1" />
                  <div>
                    <h4 className="font-semibold text-base mb-1">Seamless Onboarding Experience</h4>
                    <p className="text-sm text-gray-600">Automated workflows that help new hires hit the ground running from their first day.</p>
                  </div>
                </div>
                <div className="flex flex-col items-center lg:items-start lg:flex-row lg:space-x-3 text-center lg:text-left">
                  <CheckCircle className="w-6 h-6 text-[#0066FF] flex-shrink-0 mb-2 lg:mb-0 lg:mt-1" />
                  <div>
                    <h4 className="font-semibold text-base mb-1">Secure Document Handling</h4>
                    <p className="text-sm text-gray-600">Collect, verify, and store employee documents with enterprise-grade security.</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90"
                  onClick={() => window.location.href = '/contact'}
                >
                  Request Demo
                </Button>
              </div>
            </div>

            {/* Right - Hub & Spoke Recruitment Cards */}
            <div className="relative flex items-center justify-center min-h-[420px]">
              {/* Center Hub */}
              <div className="absolute z-20 w-24 h-24 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-full flex items-center justify-center shadow-xl shadow-blue-500/30">
                <div className="text-center">
                  <LucideIcons.Users className="w-8 h-8 text-white mx-auto mb-1" />
                  <span className="text-[10px] font-semibold text-white">HRMQore</span>
                </div>
              </div>

              {/* Connecting Lines (SVG) */}
              <svg className="absolute inset-0 w-full h-full z-10" viewBox="0 0 400 420">
                {/* Lines from center to each card - adjusted coordinates */}
                <line x1="200" y1="210" x2="200" y2="40" stroke="rgba(0,102,255,0.3)" strokeWidth="2" />
                <line x1="200" y1="210" x2="340" y2="90" stroke="rgba(0,102,255,0.3)" strokeWidth="2" />
                <line x1="200" y1="210" x2="360" y2="210" stroke="rgba(0,102,255,0.3)" strokeWidth="2" />
                <line x1="200" y1="210" x2="320" y2="360" stroke="rgba(0,102,255,0.3)" strokeWidth="2" />
                <line x1="200" y1="210" x2="80" y2="360" stroke="rgba(0,102,255,0.3)" strokeWidth="2" />
                <line x1="200" y1="210" x2="40" y2="210" stroke="rgba(0,102,255,0.3)" strokeWidth="2" />
              </svg>

              {/* Surrounding Cards - Properly Aligned */}
              {/* Top - Applications */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20">
                <div className="bg-white rounded-xl shadow-lg p-3 border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="w-10 h-10 bg-[#0066FF]/10 rounded-lg flex items-center justify-center mb-2 mx-auto">
                    <LucideIcons.FileInput className="w-5 h-5 text-[#0066FF]" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 block text-center">Applications</span>
                </div>
              </div>

              {/* Top Right - ATS */}
              <div className="absolute top-[50px] right-[15px] z-20">
                <div className="bg-white rounded-xl shadow-lg p-3 border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-2 mx-auto">
                    <LucideIcons.Filter className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 block text-center">ATS</span>
                </div>
              </div>

              {/* Right - Interviews */}
              <div className="absolute top-1/2 -translate-y-1/2 right-0 z-20">
                <div className="bg-white rounded-xl shadow-lg p-3 border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-2 mx-auto">
                    <LucideIcons.Video className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 block text-center">Interviews</span>
                </div>
              </div>

              {/* Bottom Right - Offer Letters */}
              <div className="absolute bottom-[30px] right-[40px] z-20">
                <div className="bg-white rounded-xl shadow-lg p-3 border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-2 mx-auto">
                    <LucideIcons.FileSignature className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 block text-center">Offer Letters</span>
                </div>
              </div>

              {/* Bottom Left - Onboarding */}
              <div className="absolute bottom-[30px] left-[40px] z-20">
                <div className="bg-white rounded-xl shadow-lg p-3 border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mb-2 mx-auto">
                    <LucideIcons.UserPlus className="w-5 h-5 text-teal-600" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 block text-center">Onboarding</span>
                </div>
              </div>

              {/* Left - Panelist */}
              <div className="absolute top-1/2 -translate-y-1/2 left-0 z-20">
                <div className="bg-white rounded-xl shadow-lg p-3 border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer">
                  <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center mb-2 mx-auto">
                    <LucideIcons.UsersRound className="w-5 h-5 text-rose-600" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700 block text-center">Panelist</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Dramatic & Elite Design with Carousel */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0066FF 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0066FF 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-[#0066FF] uppercase tracking-widest mb-4">
              VOICES OF TRANSFORMATION
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
              Stories from{' '}
              <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
                HR Leaders & Employees
              </span>
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              Discover how HRMQore transforms daily work experiences for both HR teams and employees across organizations.
            </p>
          </div>

          {/* Featured Testimonial Carousel */}
          <TestimonialCarousel testimonials={testimonials} />

          {/* CTA to Customer Stories */}
          <div className="text-center mt-12">
            <Button
              size="lg"
              className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90 px-8"
              onClick={() => window.location.href = '/customer-stories'}
            >
              Read More Success Stories
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section> 

      {/* CTA Section - Dynamic Animated Background */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
              {/* Floating Orbs - Animated */}
              <div className="absolute w-64 h-64 bg-[#0066FF]/30 rounded-full blur-3xl animate-pulse" style={{ top: '-10%', right: '-5%' }}></div>
              <div className="absolute w-48 h-48 bg-[#0052CC]/20 rounded-full blur-2xl animate-pulse" style={{ bottom: '10%', left: '-5%', animationDelay: '1s' }}></div>
              <div className="absolute w-32 h-32 bg-purple-500/20 rounded-full blur-2xl animate-pulse" style={{ top: '30%', left: '20%', animationDelay: '2s' }}></div>
              <div className="absolute w-40 h-40 bg-cyan-500/20 rounded-full blur-2xl animate-pulse" style={{ bottom: '20%', right: '15%', animationDelay: '0.5s' }}></div>
              
              {/* Grid Pattern */}
              <div className="absolute inset-0 opacity-5" style={{
                backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                backgroundSize: '40px 40px'
              }}></div>
              
              {/* Floating Particles */}
              <div className="absolute w-2 h-2 bg-[#0066FF] rounded-full animate-bounce" style={{ top: '20%', left: '30%', animationDuration: '3s' }}></div>
              <div className="absolute w-3 h-3 bg-cyan-400 rounded-full animate-bounce" style={{ top: '60%', right: '25%', animationDuration: '4s', animationDelay: '1s' }}></div>
              <div className="absolute w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ bottom: '30%', left: '40%', animationDuration: '3.5s', animationDelay: '0.5s' }}></div>
              <div className="absolute w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ top: '40%', right: '40%', animationDuration: '2.5s', animationDelay: '2s' }}></div>
              
              {/* Moving Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#0066FF]/5 to-transparent animate-pulse" style={{ animationDuration: '4s' }}></div>
            </div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">
                Ready to Write{' '}
                <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
                  Your Story?
                </span>
              </h2>
              <p className="text-base md:text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
                Join the community of forward-thinking companies transforming how they manage and empower their people. Your transformation story starts with a conversation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90 px-8"
                  onClick={() => window.location.href = '/contact'}
                >
                  Start Your Story
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-gray-600 text-white hover:bg-white/10"
                  onClick={() => window.location.href = '/pricing'}
                >
                  Explore Plans
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;